import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Annotorious,
  DrawingStyleExpression,
  ImageAnnotation,
  OpenSeadragonAnnotator,
  OpenSeadragonViewer,
} from '@annotorious/react';
import type OpenSeadragon from 'openseadragon';

import { AnnotatorToolbar } from '@/components/blocks/annotator/annotator-toolbar';
import { DEFAULT_OSD_OPTIONS } from '@/components/blocks/annotator/constants';
import { RubberBandSelection } from '@/components/blocks/annotator/rubber-band-selection';
import type {
  AnnotatorLabels,
  AnnotatorMode,
} from '@/components/blocks/annotator/types';
import { useAnnotationsBridge } from '@/components/blocks/annotator/use-annotations-bridge';
import { useSelectionBridge } from '@/components/blocks/annotator/use-selection-bridge';
import { cn } from '@/lib/utils';

export interface AnnotatorProps {
  image: string;
  annotations?: Array<ImageAnnotation>;
  style?: DrawingStyleExpression<ImageAnnotation>;
  readonly?: boolean;
  onAnnotationsChange?: (annotations: Array<ImageAnnotation>) => void;
  /** Ids of the annotations to keep visually selected, in selection order. */
  selectedIds?: ReadonlyArray<string>;
  /** Fired when a click replaces the selection (box click or deselect). */
  onSelectAnnotation?: (id: string | null) => void;
  /** Fired when Shift + click toggles a box in/out of the selection set. */
  onToggleAnnotation?: (id: string) => void;
  /**
   * Fired when a Shift + drag rubber band is released, with the ids of every
   * box the band intersects.
   */
  onSelectAnnotations?: (ids: Array<string>) => void;
  options?: Partial<OpenSeadragon.Options>;
  labels?: Partial<AnnotatorLabels>;
  /** Extra controls rendered in the toolbar, after the built-in tools. */
  toolbarExtras?: ReactNode;
  className?: string;
}

const AnnotatorInner = ({
  image,
  annotations,
  style,
  readonly = false,
  onAnnotationsChange,
  selectedIds,
  onSelectAnnotation,
  onToggleAnnotation,
  onSelectAnnotations,
  options: optionsProp,
  labels: labelsProp,
  toolbarExtras,
}: AnnotatorProps) => {
  const [mode, setMode] = useState<AnnotatorMode>('pan');
  const { t } = useTranslation();
  // Translated at render time — resolving these at module load (the previous
  // approach) runs before i18next has loaded its resources, leaving every
  // toolbar button with an empty title/aria-label and thus no tooltip.
  const labels: AnnotatorLabels = {
    pan: t('common.annotationTools.pan'),
    draw: t('common.annotationTools.draw'),
    zoomIn: t('common.annotationTools.zoomIn'),
    zoomOut: t('common.annotationTools.zoomOut'),
    resetZoom: t('common.annotationTools.resetZoom'),
    ...labelsProp,
  };

  const options = useMemo<OpenSeadragon.Options>(
    () => ({
      ...DEFAULT_OSD_OPTIONS,
      ...optionsProp,
      tileSources: { type: 'image', url: image },
    }),
    [image, optionsProp]
  );

  // Drawing a rectangle and panning both consume click-drag, so only enable
  // drawing in 'draw' mode; otherwise OpenSeadragon owns the drag for panning.
  const isDrawingEnabled = !readonly && mode === 'draw';

  // TODO: UseEffect is frowned upon. Improve code smell.
  // Instead of relying on useEffect for outside communication, create useImperative hook and pass on ref object
  useAnnotationsBridge({ annotations, onAnnotationsChange });
  useSelectionBridge({ selectedIds, onSelectAnnotation, onToggleAnnotation });

  return (
    <OpenSeadragonAnnotator
      tool='rectangle'
      drawingMode='drag'
      drawingEnabled={isDrawingEnabled}
      style={style}>
      <OpenSeadragonViewer
        className='h-full w-full'
        options={options}
      />
      <RubberBandSelection
        onSelectAnnotations={onSelectAnnotations}
        onToggleAnnotation={onToggleAnnotation}
      />
      <AnnotatorToolbar
        mode={mode}
        onModeChange={setMode}
        readonly={readonly}
        labels={labels}
        extras={toolbarExtras}
      />
    </OpenSeadragonAnnotator>
  );
};

const Annotator: FC<AnnotatorProps> = ({
  className,
  ...props
}: AnnotatorProps) => (
  <div
    className={cn('relative flex min-h-0 flex-1 overflow-hidden', className)}>
    <Annotorious>
      <AnnotatorInner {...props} />
    </Annotorious>
  </div>
);

export { Annotator };
